import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { marked } from "marked";
import DOMPurify from 'dompurify';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Generate URL-friendly slug from title
export function generateSlug(title) {
  if (!title) return '';

  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

// Calculate estimated read time based on content
export function calculateReadTime(content) {
  if (!content) return 0;

  // Remove HTML tags and count words
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.trim().split(/\s+/).length;

  // Average reading speed is 200-250 words per minute
  const wordsPerMinute = 225;
  const readTime = Math.ceil(words / wordsPerMinute);

  return Math.max(1, readTime); // Minimum 1 minute
}

// Create a custom renderer for better link handling
const renderer = new marked.Renderer();

// Override link rendering to add target="_blank" for external links
renderer.link = function(href, title, text) {
  // Handle null/undefined href
  if (!href) {
    href = '#';
  }

  const isExternal = typeof href === 'string' && (href.startsWith('http://') || href.startsWith('https://'));
  const target = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
  const titleAttr = title ? ` title="${title}"` : '';
  const safeHref = href || '#';

  return `<a href="${safeHref}"${titleAttr}${target}>${text}</a>`;
};

// Configure marked options for better security and formatting
marked.setOptions({
  breaks: true, // Convert line breaks to <br>
  gfm: true, // Enable GitHub Flavored Markdown
  headerIds: false, // Disable header IDs for security
  mangle: false, // Don't mangle email addresses
  pedantic: false, // Don't be overly strict
  sanitize: false, // We'll use DOMPurify instead
  smartLists: true, // Use smarter list behavior
  smartypants: false, // Don't use smart quotes
  renderer: renderer, // Use our custom renderer
});

// Convert markdown to safe HTML
export function markdownToHtml(markdown) {
  if (!markdown || typeof markdown !== 'string') return '';

  try {
    // Convert markdown to HTML
    const rawHtml = marked(markdown);

    // Ensure we have a string result
    if (typeof rawHtml !== 'string') {
      console.warn('Marked returned non-string result:', rawHtml);
      return DOMPurify.sanitize(markdown);
    }

    // Sanitize the HTML to prevent XSS attacks
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'b', 'i', 'u', 'strike', 'del',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote',
        'a', 'img',
        'code', 'pre',
        'hr', 'div', 'span'
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'class'],
      ALLOW_DATA_ATTR: false,
      // Ensure links open in new tab for external links
      FORBID_ATTR: ['style', 'onclick', 'onerror', 'onload']
    });

    return cleanHtml;
  } catch (error) {
    console.error('Error converting markdown to HTML:', error);
    // Fallback: return the original markdown as escaped HTML
    return DOMPurify.sanitize(markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  }
}
